import Client from './Client';
import { User } from '../models';
import { SPOOF_ORIGINAL_USER_STORAGE_KEY } from './spoofSession';


const LOGIN_PATH            = '/kuser/login';
const REGISTER_PATH         = '/kuser/register';
const SPOOF_PATH            = '/kuser/spoof';
const LOGOUT_PATH           = '/kuser/logout';
const GET_PROFILE_PATH      = '/kuser/profile';
const FORGOT_PASSWORD_PATH  = '/kuser/forgot_password';
const RESET_PASSWORD_PATH   = '/kuser/reset_password';
const RESET_PASSWORD_CONTEXT_PATH = '/kuser/reset_password_context';
const CONFIRM_EMAIL_PATH    = '/kuser/confirm_email_change';

/**
 * Profile validation failed for a transient reason (network, 5xx, proxy, timeout).
 * Session bootstrap should keep the stored user instead of forcing logout.
 */
export class ApiKeyCheckTransientError extends Error {
    constructor(message, status = null, cause = undefined) {
        super(message);
        this.name = 'ApiKeyCheckTransientError';
        /** @type {number|null} */
        this.status = status;
        if (cause !== undefined) {
            this.cause = cause;
        }
    }
}

class Auth extends Client {
    checkApiKey(apiKey) {
        return this.GET(GET_PROFILE_PATH, {}, {
            'X-API-Key': apiKey,
        }).then(
            (response) => this._finishProfileCheck(response),
            (err) => {
                throw new ApiKeyCheckTransientError(
                    err?.message || 'Network error during profile check',
                    null,
                    err
                );
            }
        );
    }

    /**
     * @param {Response} response
     * @returns {Promise<User|null>}
     */
    async _finishProfileCheck(response) {
        const status = response.status;

        // Kore rejects invalid API keys before the action runs (RestController::before_filter).
        if (status === 401 || status === 403) {
            return null;
        }

        if (status >= 500 || status === 429 || status === 408) {
            throw new ApiKeyCheckTransientError(
                `Profile check unavailable (HTTP ${status})`,
                status
            );
        }

        if (!response.ok) {
            throw new ApiKeyCheckTransientError(
                `Profile check failed (HTTP ${status})`,
                status
            );
        }

        let json;
        try {
            json = await response.json();
        } catch (_e) {
            throw new ApiKeyCheckTransientError(
                'Invalid response body for profile check',
                status
            );
        }

        if (json.user) {
            const { tickets, ...rest } = json.user;
            localStorage.setItem('user', JSON.stringify(rest));
            return new User(rest);
        }

        return null;
    }

    logIn(email, password) {
        return this.POST(LOGIN_PATH, {
            email,
            password
        })
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    const { tickets, ...rest } = json.user;
                    localStorage.setItem('user', JSON.stringify(rest));
                    json.user = new User(rest);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    register(payload) {
        return this.POST(REGISTER_PATH, payload)
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    const { tickets, ...rest } = json.user;
                    localStorage.setItem('user', JSON.stringify(rest));
                    json.user = new User(rest);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    spoof(id) {
        return this.POST(SPOOF_PATH, { id })
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    const currentRaw = localStorage.getItem('user');
                    if (currentRaw && currentRaw !== 'null' && !localStorage.getItem(SPOOF_ORIGINAL_USER_STORAGE_KEY)) {
                        localStorage.setItem(SPOOF_ORIGINAL_USER_STORAGE_KEY, currentRaw);
                    }
                    const rest = { ...json.user };
                    let actor_is_super_admin = false;
                    if (currentRaw && currentRaw !== 'null') {
                        try {
                            const actor_rest = JSON.parse(currentRaw);
                            actor_is_super_admin = !!(
                                actor_rest.abilities?.superadmin || actor_rest.is_super_admin
                            );
                        } catch (_e) {
                            actor_is_super_admin = false;
                        }
                    }
                    if (rest.tkt_instance_id && !actor_is_super_admin) {
                        rest.tkt_instance_ids = [rest.tkt_instance_id];
                    }
                    localStorage.setItem('user', JSON.stringify(rest));
                    json.user = new User(rest);
                    return json;
                }
                const err = json.flash?.error;
                throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : 'Spoof failed');
            });
    }

    logOut() {
        return this.POST(LOGOUT_PATH).then(response => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Déconnexion impossible");
        });
    }

    forgotPassword(email) {
        return this.POST(FORGOT_PASSWORD_PATH, { email })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const err = json.flash?.error;
                    throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : `HTTP ${response.status}`);
                }
                const err = json.flash?.error;
                if (err) {
                    throw new Error(Array.isArray(err) ? err.join(', ') : err);
                }
                return json.flash?.success ?? true;
            });
    }

    getResetPasswordContext(token) {
        return this.GET(RESET_PASSWORD_CONTEXT_PATH, { token })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const err = json.flash?.error;
                    throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : `HTTP ${response.status}`);
                }
                const err = json.flash?.error;
                if (err) {
                    throw new Error(Array.isArray(err) ? err.join(', ') : err);
                }
                return json.reset_password_context?.user_inputs ?? [];
            });
    }

    resetPassword(token, password, password_confirmation) {
        return this.POST(RESET_PASSWORD_PATH, {
            token,
            password,
            'password-confirmation': password_confirmation,
        })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const err = json.flash?.error;
                    throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : `HTTP ${response.status}`);
                }
                const err = json.flash?.error;
                if (err) {
                    throw new Error(Array.isArray(err) ? err.join(', ') : err);
                }
                return json.flash?.success ?? true;
            });
    }

    confirmEmailChange(token) {
        return this.POST(CONFIRM_EMAIL_PATH, { token })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const err = json.flash?.error;
                    throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : `HTTP ${response.status}`);
                }
                const err = json.flash?.error;
                if (err) {
                    throw new Error(Array.isArray(err) ? err.join(', ') : err);
                }
                return json.flash?.success ?? true;
            });
    }
}

const AuthApi = new Auth();

export default AuthApi;
